import { fetchQuery, graphql } from 'react-relay';

import { APIConfig } from 'src/config';
import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY, DEFAULT_FETCH_QUERY_TIMEOUT, DEFAULT_MAX_RETRIES,
  ELEC_ENERGY_CONSUMED, ELEC_ENERGY_GENERATED,
  P1D,
} from 'src/util/constants';
import { queryWithRetry } from 'src/util/graphql';
import BatchQueryError from 'src/util/graphqlErrors';
import logger from 'src/util/logger';
import {
  calculatePortfolioTradeSetSummaryBatchSize,
  generateBatchTimeRanges,
} from 'src/util/tradeSetSummary/portfolioBatchCalculator';

import { PORTFOLIO_SHOW_QUERY_TYPES } from './constants';
import { PortfolioShowLoadingActions } from '../reducer/constants';

// Portfolio uses a higher meter page size than the shared constant (100) because
// Stage 1 meter metadata is lightweight; 500 per page reduces round trips
// for large portfolios (500 meters = 1 request instead of 5).
const METER_LIST_PAGE_SIZE = 500;

// Fallback rule page size used when the portfolio rule count is unknown
// (e.g. date-range reloads via getTimeSeriesData that reuse pre-fetched meters).
// The backend does not treat `first: null` as "no limit", so an explicit large
// number is required. When ruleCount IS known (full load), it is used directly
// as rulesPageSize — a tighter and semantically correct upper bound.
//
// Load is controlled via the *meter* page size (getRulesMeterPageSize), not by
// capping rules per meter. Capping rules + re-fetching overflow meters was
// evaluated and rejected: cursor tokens are incompatible between the nested and
// per-meter resolvers (forcing full re-fetches), and the resulting sub-query
// thundering herd caused backend 500 errors at scale.
// See: docs/brainstorms/2026-04-20-portfolio-batch-size-optimization-brainstorm.md
//      (Rejected Approach, lines 156–168)
//      docs/exec-plans/active/2026-04-21-feat-portfolio-batch-size-optimization-plan.md
//      (lines 214–218, 278–280)
const FALLBACK_RULES_PAGE_SIZE = 999999999;

// GraphQL Queries
// Query 1: Meters data for a portfolio. Paginated to handle portfolios with many meters.
const portfolioMetersQuery = graphql`
query dataBatchingPortfolioMetersQuery(
  $uuid: ID!,
  $first: Int,
  $after: String,
  $start: Timestamp!,
  $finish: Timestamp!
) @throwOnFieldError {
  portfolio(uuid:$uuid) {
    meters(first: $first, after: $after, start: $start, finish: $finish) {
      edges {
        node {
          uuid
          identifier
          externalIdentifier
          active { start finish }
          title
          tradePointId
          primaryBillingPoint {
            identifier
          }
          property {
            uuid
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 2: Portfolio-level rule count. Fetched once in parallel with Stage 1 meters.
// Used by getRulesMeterPageSize to calculate the optimal meter page size for Stage 2 (Rules).
const portfolioRuleCountQuery = graphql`
query dataBatchingPortfolioRuleCountQuery(
  $uuid: ID!,
  $start: Timestamp!,
  $finish: Timestamp!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    ruleCount(start: $start, finish: $finish)
  }
}
`;

// Query 3: Portfolio-level trade set summary. Batched by time range.
const portfolioTradeSetSummaryQuery = graphql`
query dataBatchingPortfolioTradeSetSummaryQuery(
  $uuid: ID!,
  $batchStart: Timestamp!,
  $batchFinish: Timestamp!,
  $timeZone: String!,
  $tradeAggregation: String!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    tradeSetSummary(start: $batchStart, finish: $batchFinish, timeZone: $timeZone, aggregation: $tradeAggregation, groups: [SUMMARY_GROUP_TRADE_POINT, SUMMARY_GROUP_TRADE_RULE, SUMMARY_GROUP_DIRECTION, SUMMARY_GROUP_TRADE_TYPE]) {
      key {
        tradePointId
        ruleId
        direction
        type
      }
      data {
        range { start finish }
        buyerTradePointIds
        sellerTradePointIds
        directions
        types
        value
        volume
        averagePrice
        tradeCount
        intervalCount
      }
    }
  }
}
`;

// Query 3: Batched rules for a page of meters within a portfolio.
const portfolioRulesPageQuery = graphql`
query dataBatchingPortfolioRulesPageQuery(
  $uuid: ID!,
  $pageSize: Int!,
  $after: String,
  $start: Timestamp!,
  $finish: Timestamp!,
  $rulesPageSize: Int
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          rules(start: $start, finish: $finish, first: $rulesPageSize) {
            edges {
              node {
                uuid
                tradeType
                buyer {
                  userId
                  user {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  tradePoint {
                    uuid
                    type
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                seller {
                  userId
                  user {
                    uuid
                    email
                    givenName
                    familyName
                  }
                  tradePoint {
                    uuid
                    type
                    meter {
                      uuid
                      identifier
                      title
                      property {
                        uuid
                        title
                      }
                    }
                  }
                }
                state
                clauses {
                  edges {
                    node {
                      price
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start { hours minutes }
                        finish { hours minutes }
                      }
                    }
                  }
                }
                proposedAt
                proposedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                proposedById
                acceptedAt
                acceptedBy {
                  uuid
                  email
                  givenName
                  familyName
                }
                acceptedById
                start
                finish
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

// Query 4: Batched meter data (consumed/generated) for a page of meters.
const portfolioMeterDataPageQuery = graphql`
query dataBatchingPortfolioMeterDataPageQuery(
  $uuid: ID!,
  $pageSize: Int!,
  $after: String,
  $start: Timestamp!,
  $finish: Timestamp!,
  $aggregation: String!,
  $timeZone: String!,
  $metric: String!
) @throwOnFieldError {
  portfolio(uuid: $uuid) {
    meters(first: $pageSize, after: $after) {
      edges {
        node {
          uuid
          calculatedData(start: $start, finish: $finish, aggregation: $aggregation, timeZone: $timeZone, metric: $metric) {
            aggregation
            metric {
              identifier
            }
            timeZone
            timeRange {
              start
              finish
            }
            data {
              timestamp
              value
              flags {
                identifier
                description
                Valid
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
}
`;

/**
 * Helper to wrap fetchQuery in a Promise with unsubscribe support for queryWithRetry.
 * @param {object} environment - Relay environment
 * @param {object} query - GraphQL query
 * @param {object} variables - Query variables
 * @returns {Promise} Promise with unsubscribe method
 */
const getQueryPromise = (environment, query, variables) => {
  let resolvePromise;
  let rejectPromise;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  const subscription = fetchQuery(environment, query, variables).subscribe({
    next: (result) => { resolvePromise(result); },
    error: (error) => { rejectPromise(error); },
  });

  promise.unsubscribe = () => subscription.unsubscribe();
  return promise;
};

// Cost model constants for meter page sizing (same as batchCalculator.js Property-level)
const MAX_RECORDS_PER_BATCH_PT30M = 8388; // payload limited: floor(1MB / 125B)
const MAX_RECORDS_PER_BATCH_P1D = 2000; // time limited: floor(20000ms / 10ms)
const MAX_METERS_PER_PAGE = 200;
const MIN_METERS_PER_PAGE = 10;

// Cost model constants for rules meter page sizing.
// Target: max rules per Stage 2 page, derived from empirical observation —
// 737 rules across 144 meters completed in ~14s on sandpit.
const MAX_RULES_PER_PAGE = 750;
const MIN_RULES_METERS_PER_PAGE = 5;

/**
 * Calculate the optimal meter page size for batched stage queries.
 *
 * Uses the cost model from batchCalculator.js (Property-level per-record costs)
 * to determine how many meters can be fetched in a single request without
 * exceeding the backend time or payload budget.
 *
 * **Algorithm (3 steps):**
 *
 * 1. **Raw capacity** — how many meters fit in one request:
 * `maxMetersPerPage = floor(maxRecordsPerBatch / intervalsInRange)`
 * where `maxRecordsPerBatch` is aggregation-specific:
 * - PT30M → 8,388 (payload limited: floor(1 MB / 125 B per record))
 * - P1D   → 2,000 (time limited: floor(20,000 ms / 10 ms per record))
 *
 * 2. **Clamp** — enforce bounds:
 * `capped = clamp(maxMetersPerPage, MIN_METERS_PER_PAGE=10, MAX_METERS_PER_PAGE=200)`
 *
 * 3. **Even distribution** — spread meters evenly across pages to avoid
 * one full page followed by a tiny remainder:
 * ```
 * pageCount = ceil(meterCount / capped)
 * pageSize  = ceil(meterCount / pageCount)
 * ```
 *
 * **Examples (all 3 stages share the same page size):**
 *
 * | Meters | Range  | Agg   | Raw cap | Capped | Pages | Page size       |
 * |--------|--------|-------|---------|--------|-------|-----------------|
 * | 200    | 7-day  | P1D   | 285     | 200    | 1     | 200 (one page)  |
 * | 200    | 30-day | P1D   | 66      | 66     | 4     | 50 (4 × 50)     |
 * | 201    | 7-day  | P1D   | 285     | 200    | 2     | 101 (101 + 100) |
 * | 101    | 30-day | P1D   | 66      | 66     | 2     | 51 (51 + 50)    |
 * | 285    | 7-day  | P1D   | 285     | 200    | 2     | 143 (143 + 142) |
 * | 200    | 2-day  | PT30M | 87      | 87     | 3     | 67 (67+67+66)   |
 * @param {number} meterCount - Total number of meters in the portfolio.
 * @param {number} intervalsInRange - Number of time intervals in the selected
 *   date range (e.g. 7 for a 7-day P1D range, 96 for a 2-day PT30M range).
 * @param {string} aggregation - Aggregation type ('PT30M' or 'P1D').
 * @returns {number} The optimal page size (number of meters per request).
 *   Returns 0 when meterCount is 0 or invalid.
 */
export const calculateMeterPageSize = (meterCount, intervalsInRange, aggregation) => {
  if (!meterCount || meterCount <= 0) return 0;
  if (!intervalsInRange || intervalsInRange <= 0) return Math.min(meterCount, MAX_METERS_PER_PAGE);

  const maxRecordsPerBatch = aggregation === P1D
    ? MAX_RECORDS_PER_BATCH_P1D
    : MAX_RECORDS_PER_BATCH_PT30M;

  const maxMetersPerPage = Math.floor(maxRecordsPerBatch / intervalsInRange);
  const capped = Math.max(MIN_METERS_PER_PAGE, Math.min(maxMetersPerPage, MAX_METERS_PER_PAGE));

  // Distribute meters evenly across pages instead of one full + one small.
  // e.g. 285 meters with cap 200 → 2 pages of 143 instead of 200 + 85.
  const pageCount = Math.ceil(meterCount / capped);
  return Math.ceil(meterCount / pageCount);
};

/**
 * Calculate the optimal meter page size for Stage 2 (Rules) based on
 * portfolio-level ruleCount from the GraphQL schema.
 *
 * Unlike energy data (where page size depends on time range), rules page size
 * depends on rule density. This function uses the portfolio's `ruleCount`
 * field and a per-page rule target (MAX_RULES_PER_PAGE = 750) to determine
 * how many meters can be batched per Stage 2 request.
 *
 * **Algorithm:**
 * 1. Calculate average rules per meter: `ruleCount / meterCount`
 * 2. Calculate max meters per page: `floor(MAX_RULES_PER_PAGE / avgRulesPerMeter)`
 * 3. Clamp to [MIN_RULES_METERS_PER_PAGE, meterCount]
 * 4. Distribute evenly across pages (same as calculateMeterPageSize)
 *
 * **Examples (MAX_RULES_PER_PAGE = 750):**
 *
 * | Meters | ruleCount | Avg rules/meter | Meters/page | Pages | Requests |
 * |--------|-----------|-----------------|-------------|-------|----------|
 * | 150    | 500       | 3.3             | 150 (all)   | 1     | 1        |
 * | 150    | 1000      | 6.7             | 112         | 2     | 2        |
 * | 150    | 2000      | 13.3            | 56          | 3     | 3        |
 * | 285    | 1000      | 3.5             | 214         | 2     | 2        |
 * | 285    | 5000      | 17.5            | 42          | 7     | 7        |
 * @param {?number} ruleCount - Portfolio-level rule count from
 *   `portfolio.ruleCount`. When null/undefined (rule density unknown, e.g. the
 *   date-change reload path), meters per page are capped at
 *   `floor(MAX_RULES_PER_PAGE / meterCount)` — scaling conservatively with
 *   portfolio size — so large portfolios still split into multiple pages.
 * @param {number} meterCount - Total number of meters in the portfolio.
 * @returns {number} The optimal meter page size for Stage 2.
 *   Returns 0 when meterCount is 0 or invalid.
 */
export const getRulesMeterPageSize = (ruleCount, meterCount) => {
  if (!meterCount || meterCount <= 0) return 0;

  // ruleCount unknown — cap at floor(MAX_RULES_PER_PAGE / meterCount) meters
  // per page so the fallback scales with portfolio size: larger portfolios get
  // smaller pages and are never batched into one giant request.
  if (ruleCount == null) {
    const fallbackPageSize = Math.max(1, Math.floor(MAX_RULES_PER_PAGE / meterCount));
    const pageCount = Math.ceil(meterCount / fallbackPageSize);
    return Math.ceil(meterCount / pageCount);
  }

  // Genuinely zero (or negative) rules — nothing to page, fetch in one request.
  if (ruleCount <= 0) return meterCount;

  const avgRulesPerMeter = ruleCount / meterCount;

  // All meters fit in one page — avg rules per meter allows it
  if (avgRulesPerMeter * meterCount <= MAX_RULES_PER_PAGE) {
    return meterCount;
  }

  const rawPageSize = Math.floor(MAX_RULES_PER_PAGE / avgRulesPerMeter);
  const capped = Math.max(MIN_RULES_METERS_PER_PAGE, Math.min(rawPageSize, meterCount));

  // Distribute meters evenly across pages (same pattern as calculateMeterPageSize)
  const pageCount = Math.ceil(meterCount / capped);
  return Math.ceil(meterCount / pageCount);
};

/**
 * Reshape rules batch page response into the format expected by mergePortfolioBatchedData.
 * No rulesPageSize limit is applied — all rules for each meter are fetched.
 * @param {Array} meterEdges - Meter edges from the batch response.
 * @returns {Array} Array of { uuid, rules: { edges } } objects.
 */
const getRulesPageData = (meterEdges) => (
  meterEdges.map((edge) => ({
    uuid: edge.node.uuid,
    rules: { edges: edge.node?.rules?.edges || [] },
  }))
);

/**
 * Reshape meter data batch page response into the format expected by mergePortfolioBatchedData.
 * Maps calculatedData → dataConsumed or dataGenerated, with empty fallback for null data.
 * @param {Array} meterEdges - Meter edges from the batch response.
 * @param {string} dataKey - DATA_CONSUMED_KEY or DATA_GENERATED_KEY.
 * @param {object} fallbackMeta - Metadata for empty fallback shape.
 * @param {string} fallbackMeta.historianAggregation - Aggregation level.
 * @param {string} fallbackMeta.metric - Metric identifier.
 * @param {string} fallbackMeta.timezone - Timezone.
 * @param {number} fallbackMeta.batchStart - Start time in epoch seconds.
 * @param {number} fallbackMeta.batchFinish - Finish time in epoch seconds.
 * @returns {Array} Array of { uuid, dataConsumed/dataGenerated } objects.
 */
const reshapeMeterDataPage = (meterEdges, dataKey, fallbackMeta) => (
  meterEdges.map((edge) => {
    const { node } = edge;
    const calcData = node?.calculatedData;

    if (!calcData) {
      return {
        uuid: node.uuid,
        [dataKey]: {
          aggregation: fallbackMeta.historianAggregation,
          metric: { identifier: fallbackMeta.metric },
          timeZone: fallbackMeta.timezone,
          timeRange: { start: fallbackMeta.batchStart, finish: fallbackMeta.batchFinish },
          data: [],
        },
      };
    }

    return {
      uuid: node.uuid,
      [dataKey]: calcData,
    };
  })
);

/**
 * Increment a mutable ref's `.current` by 1 and return the new value.
 * @param {object} ref - Mutable ref object { current: number }.
 * @returns {number} The incremented value.
 */
const incrementRef = (ref) => {
  const mutableRef = ref;
  mutableRef.current += 1;
  return mutableRef.current;
};

/**
 * Signal cross-stage cancellation by setting the cancelled ref.
 * @param {object} ref - Cancellation token { current: boolean }.
 */
const signalCancellation = (ref) => {
  const mutableRef = ref;
  mutableRef.current = true;
};

/**
 * Fetch all pages of a batched stage query, accumulating reshaped results.
 * Uses recursion to paginate without await-in-loop (matching getPortfolioMeters pattern).
 * @param {object} params
 * @param {object} params.query - The GraphQL query to execute.
 * @param {object} params.baseVariables - Base query variables (without cursor).
 * @param {number} params.pageSize - Number of meters per page.
 * @param {string} params.stageName - Stage name for logging.
 * @param {string} params.queryType - Query-type tag (a member of
 *   `PORTFOLIO_SHOW_QUERY_TYPES`) applied to a `BatchQueryError` when a page
 *   fetch fails, so the fallback UI can render a query-specific message.
 * @param {Function} params.reshapeFunc - Function to reshape page response into output format.
 * @param {object} params.environment - Relay environment.
 * @param {object} [params.cancelledRef] - User-owned cancellation token { current: boolean }.
 * @param {object} [params.stageAbortRef] - Internal cross-stage abort token
 *   { current: boolean }; set when any stage fails so sibling stages stop
 *   paginating. Kept separate from cancelledRef so a stage failure is not
 *   mistaken for a user cancellation.
 * @param {object} [params.progressRef] - Shared mutable ref { current: number } for progress.
 * @param {number} [params.meterBatchesTotal] - Total batches across all stages for progress.
 * @param {Function} [params.dispatchFunc] - Reducer dispatch for progress updates.
 * @returns {Promise<Array>} Accumulated reshaped results across all pages.
 */
const getStagePages = async ({
  query, baseVariables, pageSize, stageName, queryType, reshapeFunc,
  environment, cancelledRef, stageAbortRef, progressRef, meterBatchesTotal, dispatchFunc,
}) => {
  const fetchPage = async (accumulated, afterCursor, pageIndex) => {
    if (cancelledRef?.current || stageAbortRef?.current) {
      logger.log(`${stageName}: Cancelled, stopping pagination`);
      return accumulated;
    }

    const variables = {
      ...baseVariables,
      pageSize,
      ...(afterCursor && { after: afterCursor }),
    };

    let data;
    try {
      data = await queryWithRetry({
        queryFn: () => getQueryPromise(environment, query, variables),
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `${stageName} page ${pageIndex + 1}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });
    } catch (error) {
      logger.error(`${stageName}: Fatal error on page ${pageIndex + 1}:`, error);
      // Signal abort via stageAbortRef (not cancelledRef) so the failure is not mistaken
      // for a user cancellation.
      if (stageAbortRef) {
        signalCancellation(stageAbortRef);
      }
      throw new BatchQueryError(queryType, error);
    }

    const metersConnection = data?.portfolio?.meters;
    if (!metersConnection?.edges) {
      logger.warn(`${stageName}: No meters data on page ${pageIndex + 1}`);
      return accumulated;
    }

    const { edges } = metersConnection;

    // Guard: stop if the backend returned an empty page (e.g. hasNextPage
    // was incorrectly true on the previous page).
    if (edges.length === 0) {
      logger.log(`${stageName}: Empty page ${pageIndex + 1}, stopping pagination`);
      return accumulated;
    }

    const pageResults = await reshapeFunc(edges);
    const allResults = [...accumulated, ...pageResults];
    const currentPage = pageIndex + 1;

    logger.log(`${stageName}: Page ${currentPage} fetched (${edges.length} meters, total accumulated: ${allResults.length})`);

    // Update progress
    if (progressRef && dispatchFunc && !cancelledRef?.current) {
      const loaded = incrementRef(progressRef);
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS,
        meterBatchesLoaded: loaded,
        meterBatchesTotal,
      });
    }

    const { pageInfo } = metersConnection;
    const nextCursor = pageInfo?.endCursor || '';
    const hasMore = pageInfo?.hasNextPage === true && nextCursor !== '';

    if (hasMore) {
      return fetchPage(allResults, nextCursor, currentPage);
    }
    return allResults;
  };

  return fetchPage([], null, 0);
};

/**
 * Calculate total rules count across all meters
 * @param {Array} rulesData - Array of rules data objects with rules property
 * @returns {number} Total count of rules across all meters
 */
export const getTotalRulesCount = (rulesData) => {
  if (!rulesData || !Array.isArray(rulesData)) {
    return 0;
  }

  return rulesData.reduce((sum, meterRules) => {
    const rulesCount = meterRules?.rules?.edges?.length || 0;
    return sum + rulesCount;
  }, 0);
};

/**
 * Merges portfolio data from the five-stage fetch into the shape expected by buildMainData.
 * Output shape: { portfolio: { meters: { edges: [...] }, tradeSetSummary: [...] } }
 * @param {object} initialData - Base portfolio data (static fields)
 * @param {Array} meters - Array of meter objects from stage 1
 * @param {Array} rulesData - Array of { uuid, rules: { edges } } from stage 2
 * @param {Array} consumedData - Array of { uuid, dataConsumed } from stage 3
 * @param {Array} generatedData - Array of { uuid, dataGenerated } from stage 4
 * @param {Array} tradeSummaryBatches - Array of tradeSetSummary arrays from stage 5 batches
 * @returns {object} Merged portfolio data
 */
export const mergePortfolioBatchedData = (
  initialData,
  meters,
  rulesData,
  consumedData,
  generatedData,
  tradeSummaryBatches,
) => {
  const validMeters = Array.isArray(meters) ? meters : [];
  const validRulesData = Array.isArray(rulesData) ? rulesData : [];
  const validConsumedData = Array.isArray(consumedData) ? consumedData : [];
  const validGeneratedData = Array.isArray(generatedData) ? generatedData : [];
  const validBatches = Array.isArray(tradeSummaryBatches) ? tradeSummaryBatches : [];

  logger.log(`Merging five-stage data: ${validMeters.length} meters, ${validRulesData.length} rules, ${validConsumedData.length} consumed, ${validGeneratedData.length} generated, ${validBatches.length} trade summary batches`);

  // Create maps for efficient lookups
  const rulesByMeterId = new Map();
  const consumedByMeterId = new Map();
  const generatedByMeterId = new Map();

  validRulesData.forEach((meterRules) => {
    if (meterRules.uuid) {
      rulesByMeterId.set(meterRules.uuid, meterRules.rules);
    }
  });

  validConsumedData.forEach((meterConsumed) => {
    if (meterConsumed.uuid) {
      consumedByMeterId.set(meterConsumed.uuid, meterConsumed[DATA_CONSUMED_KEY]);
    }
  });

  validGeneratedData.forEach((meterGenerated) => {
    if (meterGenerated.uuid) {
      generatedByMeterId.set(meterGenerated.uuid, meterGenerated[DATA_GENERATED_KEY]);
    }
  });

  // Merge per-meter data
  const mergedEdges = validMeters.map((meter) => {
    const meterId = meter.uuid;
    return {
      node: {
        ...meter,
        rules: rulesByMeterId.get(meterId) || { edges: [] },
        [DATA_CONSUMED_KEY]: consumedByMeterId.get(meterId) || { data: [] },
        [DATA_GENERATED_KEY]: generatedByMeterId.get(meterId) || { data: [] },
      },
    };
  });

  // Merge portfolio-level trade set summary across all batches.
  // Each batch returns an array of trade summaries. We need to merge entries
  // with the same key (tradePointId, ruleId, direction, type) by concatenating data arrays.
  const tradeMap = new Map();

  validBatches.forEach((batchSummaries) => {
    if (!Array.isArray(batchSummaries)) return;

    batchSummaries.forEach((trade) => {
      if (!trade?.key) return;
      const {
        tradePointId, ruleId, direction, type,
      } = trade.key;
      const mapKey = `${tradePointId || ''}_${ruleId || ''}_${direction || ''}_${type || ''}`;

      if (tradeMap.has(mapKey)) {
        const existing = tradeMap.get(mapKey);
        if (Array.isArray(existing.data) && Array.isArray(trade.data)) {
          existing.data = [...existing.data, ...trade.data];
        }
      } else {
        tradeMap.set(mapKey, { ...trade, data: [...(trade.data || [])] });
      }
    });
  });

  const mergedTradeSetSummary = Array.from(tradeMap.values());

  // Shallow copy static portfolio fields — only top-level properties are
  // accessed (uuid, title, active, etc.). meters and tradeSetSummary are
  // overwritten below, so deep cloning is unnecessary.
  const basePortfolio = { ...(initialData?.portfolio || {}) };

  return {
    portfolio: {
      ...basePortfolio,
      meters: { edges: mergedEdges },
      tradeSetSummary: mergedTradeSetSummary,
    },
  };
};

/**
 * Stage 1: Fetch all meters for a portfolio with pagination support.
 * Uses recursion to paginate without await-in-loop.
 * @param {object} args - Arguments for fetching meters
 * @param {string} args.portfolioId - Portfolio ID
 * @param {object} args.environment - Relay environment
 * @param {number} args.startSeconds - Start timestamp in epoch seconds
 * @param {number} args.finishSeconds - Finish timestamp in epoch seconds
 * @param {number} [args.pageSize=METER_LIST_PAGE_SIZE] - Page size
 * @returns {Promise<{meters: Array}>} Paginated meters for the portfolio
 */
export const getPortfolioMeters = async (args) => {
  const {
    portfolioId,
    environment,
    startSeconds,
    finishSeconds,
    pageSize = METER_LIST_PAGE_SIZE,
  } = args;

  const fetchPage = async (accumulated, afterCursor) => {
    const variables = {
      uuid: portfolioId,
      first: pageSize,
      start: startSeconds,
      finish: finishSeconds,
      ...(afterCursor && { after: afterCursor }),
    };

    try {
      const data = await queryWithRetry({
        queryFn: () => getQueryPromise(environment, portfolioMetersQuery, variables),
        timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
        operationName: `Fetching meters for portfolio ${portfolioId}`,
        maxRetries: DEFAULT_MAX_RETRIES,
      });

      if (!data?.portfolio?.meters?.edges) {
        logger.warn('No meters data returned');
        return accumulated;
      }

      const { edges } = data.portfolio.meters;

      // Guard: stop if the backend returned an empty page (e.g. hasNextPage
      // was incorrectly true on the previous page).
      if (edges.length === 0) {
        logger.log('Stage 1: Empty page, stopping pagination');
        return accumulated;
      }

      const pageMeters = edges.map((edge) => edge.node);
      const allMeters = [...accumulated, ...pageMeters];
      logger.log(`Stage 1: Fetched ${pageMeters.length} meters (total: ${allMeters.length})`);

      const { pageInfo } = data.portfolio.meters;
      const nextCursor = pageInfo?.endCursor || '';
      const hasMore = pageInfo?.hasNextPage === true && nextCursor !== '';

      if (hasMore) {
        return fetchPage(allMeters, nextCursor);
      }
      return allMeters;
    } catch (error) {
      logger.error('Error fetching portfolio meters page:', error);
      // Tag with the METERS stage so the fallback UI can render a
      // query-specific message. Re-throwing propagates to the outer
      // try/catch in getPortfolioDataInBatches and on to onError.
      throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METERS, error);
    }
  };

  const allMeters = await fetchPage([], '');
  logger.log(`getPortfolioMeters retrieved ${allMeters.length} meters`);
  return { meters: allMeters };
};

/**
 * Fetch the portfolio-level rule count for a given time range.
 * Runs once in parallel with Stage 1 (meters) in getPortfolioDataInBatches.
 * The result is used by getRulesMeterPageSize to calculate the optimal meter
 * page size for Stage 2 (Rules).
 * @param {object} args - Arguments
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.environment - Relay environment
 * @param {number} args.startSeconds - Start timestamp in epoch seconds
 * @param {number} args.finishSeconds - Finish timestamp in epoch seconds
 * @returns {Promise<number>} Portfolio rule count (0 when unavailable)
 * @throws {BatchQueryError} Tagged with `PORTFOLIO_SHOW_QUERY_TYPES.METERS`
 *   when the query fails, so the failure aborts parallel Stage 1 execution
 *   and surfaces to the `PlaceholderErrorModal` flow.
 */
export const getPortfolioRuleCount = async (args) => {
  const {
    portfolioId,
    environment,
    startSeconds,
    finishSeconds,
  } = args;

  const variables = {
    uuid: portfolioId,
    start: startSeconds,
    finish: finishSeconds,
  };

  try {
    const data = await queryWithRetry({
      queryFn: () => getQueryPromise(environment, portfolioRuleCountQuery, variables),
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching rule count for portfolio ${portfolioId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    return data?.portfolio?.ruleCount ?? 0;
  } catch (error) {
    logger.error('Error fetching portfolio rule count:', error);
    // Tag with the METERS stage (consistent with the sibling Stage 1 meters
    // fetch) and re-throw so a field error aborts parallel Stage 1 execution
    // and surfaces to useBatchedFetchErrorState, rather than silently
    // proceeding with an incorrect ruleCount of 0.
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.METERS, error);
  }
};

/**
 * Stage 5: Fetch portfolio-level trade set summary for a single time batch.
 * @param {object} args - Arguments for fetching trade set summary
 * @param {string} args.portfolioId - Portfolio ID
 * @param {number} args.batchStart - Batch start time (epoch seconds)
 * @param {number} args.batchFinish - Batch finish time (epoch seconds)
 * @param {object} args.environment - Relay environment
 * @param {string} args.tradeAggregation - Aggregation level
 * @param {string} args.timezone - Timezone
 * @param {object} [args.cancelledRef] - User-owned cancellation token { current: boolean }.
 * @param {object} [args.stageAbortRef] - Internal cross-stage abort token
 *   { current: boolean }; set when any stage fails so sibling stages stop
 *   paginating. Kept separate from cancelledRef so a stage failure is not
 *   mistaken for a user cancellation.
 * @returns {Promise<Array>} Array of trade set summary objects
 */
export const getPortfolioTradeSetSummaryBatch = async (args) => {
  const {
    portfolioId,
    batchStart,
    batchFinish,
    environment,
    tradeAggregation,
    timezone,
    cancelledRef,
    stageAbortRef,
  } = args;

  if (cancelledRef?.current || stageAbortRef?.current) {
    logger.log(`Stage 5: Cancelled, skipping batch (${batchStart} - ${batchFinish})`);
    return [];
  }

  const variables = {
    uuid: portfolioId,
    batchStart,
    batchFinish,
    timeZone: timezone,
    tradeAggregation,
  };

  logger.log(`Stage 5: Fetching trade set summary for portfolio ${portfolioId} (${batchStart} - ${batchFinish})`);

  try {
    const data = await queryWithRetry({
      queryFn: () => getQueryPromise(
        environment,
        portfolioTradeSetSummaryQuery,
        variables,
      ),
      timeoutMs: APIConfig().GRAPH_API_TIMEOUT || DEFAULT_FETCH_QUERY_TIMEOUT,
      operationName: `Fetching trade set summary batch for portfolio ${portfolioId}`,
      maxRetries: DEFAULT_MAX_RETRIES,
    });

    if (!data?.portfolio?.tradeSetSummary) {
      logger.warn('No trade set summary returned for batch');
      return [];
    }

    const summaryCount = data.portfolio.tradeSetSummary.length;
    logger.log(`Stage 5: Received ${summaryCount} trade set summary entries`);
    return data.portfolio.tradeSetSummary;
  } catch (error) {
    logger.error('Error fetching portfolio trade set summary batch:', error);
    // Abort sibling stages via the internal flag — NOT the user-owned
    // cancelledRef, matching the pattern in getStagePages.
    if (stageAbortRef) {
      signalCancellation(stageAbortRef);
    }
    // Tag with the TRADE_SET_SUMMARY stage and re-throw so the failure is
    // surfaced to the consumer's onError (rather than silently producing an
    // empty summary), matching the field-error handling in the other stages.
    throw new BatchQueryError(PORTFOLIO_SHOW_QUERY_TYPES.TRADE_SET_SUMMARY, error);
  }
};

/**
 * Stages 2-5: fetch rules, consumed, generated, and trade summaries — all in parallel.
 * Stage 5 (trade summary) uses the BE-provided portfolio.ruleCount from Stage 1 for
 * batch sizing, so it starts immediately alongside Stages 2-4 instead of waiting for
 * Stage 2's actual rule edges.
 * Shared by getPortfolioDataInBatches (full load) and getTimeSeriesData (date-change reload).
 * @param {object} params
 * @param {string} params.portfolioId - Portfolio UUID
 * @param {Array}  params.meters - Pre-fetched meter objects from stage 1
 * @param {object} params.dateSpan - { start, finish } Luxon DateTimes
 * @param {string} params.timezone - Timezone string
 * @param {object} params.environment - Relay environment
 * @param {string} params.historianAggregation - Aggregation for energy data
 * @param {string} params.tradeAggregation - Aggregation for trade data
 * @param {Function} [params.dispatchFunc] - Reducer dispatch
 * @param {object}   [params.cancelledRef] - Cancellation token { current: boolean }
 * @param {number}   [params.ruleCount] - Portfolio-level rule count from the dedicated
 *   ruleCount query. Used as the rulesPageSize upper bound (tighter than the fallback
 *   999999999) and to size the Stage 2 meter batches via getRulesMeterPageSize.
 *   When omitted (e.g. date-range reload), both fall back to their respective defaults.
 * @returns {Promise<object|null>} { rulesData, consumedData, generatedData, tradeSummaryBatches,
 *   totalRulesCount, batchCount } or null if cancelled
 */
const fetchTimeSeriesStages = async ({
  portfolioId, meters, dateSpan, timezone, environment,
  historianAggregation, tradeAggregation, dispatchFunc, cancelledRef,
  ruleCount,
}) => {
  // No meters — nothing to batch. Short-circuit to avoid issuing
  // portfolio.meters(first: 0) queries, which fail GraphQL validation.
  if (!meters || meters.length === 0) {
    logger.log('fetchTimeSeriesStages: no meters, skipping stages 2-5');
    return {
      rulesData: [],
      consumedData: [],
      generatedData: [],
      tradeSummaryBatches: [],
      totalRulesCount: 0,
      batchCount: 0,
    };
  }

  const { start, finish } = dateSpan || {};
  const startSeconds = Math.floor(start.toSeconds());
  const finishSeconds = Math.floor(finish.toSeconds());

  // Calculate dynamic page size for energy stages (time-range-aware)
  const durationHours = finish.diff(start, 'hours').hours;
  const intervalHours = historianAggregation === P1D ? 24 : 0.5;
  const intervalsInRange = Math.ceil(durationHours / intervalHours);
  const meterCount = meters.length;

  // No floor on stage page sizes — the empty-page guard in getStagePages
  // handles the backend's spurious hasNextPage bug (terminates pagination
  // when a page returns 0 edges). This allows rule-density batching to split
  // large portfolios into smaller, faster pages.
  const energyPageSize = calculateMeterPageSize(meterCount, intervalsInRange, historianAggregation);

  // Calculate decoupled page size for rules stage (rule-density-aware)
  const rulesMeterPageSize = getRulesMeterPageSize(ruleCount, meterCount);

  // Progress tracking: account for different page counts per stage
  const rulesPages = Math.ceil(meterCount / Math.max(rulesMeterPageSize, 1));
  const energyPagesPerStage = Math.ceil(meterCount / Math.max(energyPageSize, 1));
  const meterBatchesTotal = rulesPages + (2 * energyPagesPerStage);

  // Shared progress counter across all three stages (single-threaded JS — no race)
  const progressRef = { current: 0 };

  const stageAbortRef = { current: false };

  // Initialize meter batch progress
  if (dispatchFunc && !cancelledRef?.current) {
    dispatchFunc({
      type: PortfolioShowLoadingActions.SET_METER_DATA_BATCH_PROGRESS,
      meterBatchesLoaded: 0,
      meterBatchesTotal,
    });
  }

  // Common variables for all stages
  const commonVariables = {
    uuid: portfolioId,
    start: startSeconds,
    finish: finishSeconds,
  };

  // Stage 5 batch sizing is computed up-front (before any stage promise is
  // created) so a synchronous throw here cannot strand the already-created
  // stage promises as unhandled rejections.
  // Uses ruleCount from Stage 1 (portfolio.ruleCount) for batch sizing so it
  // can start immediately — no need to wait for Stage 2's actual rule edges.
  // The BE-provided ruleCount is a lower bound (shared rules counted once),
  // which produces slightly larger batches — the safe direction.
  // When ruleCount is unknown (date-change reload path), fall back to a fixed
  // conservative MAX_RULES_PER_PAGE rather than meterCount, which would
  // under-estimate rule volume and risk overly large time batches.
  const tradeSummaryRuleCount = ruleCount ?? MAX_RULES_PER_PAGE;
  const batchCalculationResult = calculatePortfolioTradeSetSummaryBatchSize({
    start,
    finish,
    aggregation: historianAggregation,
    ruleCount: tradeSummaryRuleCount,
    meterCount,
  });

  const timeBatches = generateBatchTimeRanges({
    start,
    finish,
    batchSizeHours: batchCalculationResult.batchSizeHours,
  });

  const batchCount = timeBatches.length;

  // Dispatch initial progress so the overlay shows batch count immediately
  if (dispatchFunc && !cancelledRef?.current) {
    dispatchFunc({
      type: PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
      batchesLoaded: 0,
      batchesTotal: batchCount,
    });
  }

  // Stages 2-5: All four streams start concurrently via Promise.all.
  // Stage 2 uses rulesMeterPageSize (rule-density-aware), Stages 3 & 4 use
  // energyPageSize (time-range-aware). rulesPageSize uses the known ruleCount
  // as a tight upper bound; falls back to FALLBACK_RULES_PAGE_SIZE when
  // ruleCount is unavailable (date-range reload path).
  const rulesPageSize = Math.max(ruleCount ?? FALLBACK_RULES_PAGE_SIZE, 1);
  const rulesPromise = getStagePages({
    query: portfolioRulesPageQuery,
    baseVariables: { ...commonVariables, rulesPageSize },
    pageSize: rulesMeterPageSize,
    stageName: 'Stage 2 (Rules)',
    queryType: PORTFOLIO_SHOW_QUERY_TYPES.RULES,
    reshapeFunc: (edges) => getRulesPageData(edges),
    environment,
    cancelledRef,
    stageAbortRef,
    progressRef,
    meterBatchesTotal,
    dispatchFunc,
  });

  const consumedPromise = getStagePages({
    query: portfolioMeterDataPageQuery,
    baseVariables: {
      ...commonVariables,
      aggregation: historianAggregation,
      timeZone: timezone,
      metric: ELEC_ENERGY_CONSUMED,
    },
    pageSize: energyPageSize,
    stageName: 'Stage 3 (Consumed)',
    queryType: PORTFOLIO_SHOW_QUERY_TYPES.METER_DATA,
    reshapeFunc: (edges) => reshapeMeterDataPage(edges, DATA_CONSUMED_KEY, {
      historianAggregation,
      metric: ELEC_ENERGY_CONSUMED,
      timezone,
      batchStart: startSeconds,
      batchFinish: finishSeconds,
    }),
    environment,
    cancelledRef,
    stageAbortRef,
    progressRef,
    meterBatchesTotal,
    dispatchFunc,
  });

  const generatedPromise = getStagePages({
    query: portfolioMeterDataPageQuery,
    baseVariables: {
      ...commonVariables,
      aggregation: historianAggregation,
      timeZone: timezone,
      metric: ELEC_ENERGY_GENERATED,
    },
    pageSize: energyPageSize,
    stageName: 'Stage 4 (Generated)',
    queryType: PORTFOLIO_SHOW_QUERY_TYPES.METER_DATA,
    reshapeFunc: (edges) => reshapeMeterDataPage(edges, DATA_GENERATED_KEY, {
      historianAggregation,
      metric: ELEC_ENERGY_GENERATED,
      timezone,
      batchStart: startSeconds,
      batchFinish: finishSeconds,
    }),
    environment,
    cancelledRef,
    stageAbortRef,
    progressRef,
    meterBatchesTotal,
    dispatchFunc,
  });

  // Stage 5: Fetch portfolio-level trade set summary in time batches using the
  // batch ranges computed up-front above.
  let batchesCompleted = 0;
  const tradeSummaryPromise = Promise.all(
    timeBatches.map((batch, index) => getPortfolioTradeSetSummaryBatch({
      portfolioId,
      batchStart: Math.floor(batch.start.toSeconds()),
      batchFinish: Math.floor(batch.finish.toSeconds()),
      environment,
      tradeAggregation,
      timezone,
      cancelledRef,
      stageAbortRef,
    }).then((batchResult) => {
      batchesCompleted += 1;
      logger.log(`Stage 5: Batch ${index + 1}/${batchCount} complete`);
      if (dispatchFunc && !cancelledRef?.current) {
        dispatchFunc({
          type: PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
          batchesLoaded: batchesCompleted,
          batchesTotal: batchCount,
        });
      }
      return batchResult;
    })),
  );

  // Run all five stages in parallel: Stages 2-4 (rules, consumed, generated)
  // and Stage 5 (trade summary) start concurrently.
  const [rulesData, consumedData, generatedData, tradeSummaryBatches] = await Promise.all([
    rulesPromise.then((data) => {
      const totalRules = getTotalRulesCount(data);
      if (dispatchFunc && !cancelledRef?.current) {
        dispatchFunc({
          type: PortfolioShowLoadingActions.SET_RULES_LOADED,
          rulesCount: totalRules,
        });
      }
      return data;
    }),
    consumedPromise.then((data) => {
      logger.log(`Stage 3 complete: ${data.length} consumed data sets`);
      if (dispatchFunc && !cancelledRef?.current) {
        dispatchFunc({ type: PortfolioShowLoadingActions.SET_CONSUMED_DATA_LOADED });
      }
      return data;
    }),
    generatedPromise.then((data) => {
      logger.log(`Stage 4 complete: ${data.length} generated data sets`);
      if (dispatchFunc && !cancelledRef?.current) {
        dispatchFunc({ type: PortfolioShowLoadingActions.SET_GENERATED_DATA_LOADED });
      }
      return data;
    }),
    tradeSummaryPromise,
  ]);

  if (cancelledRef?.current) {
    logger.log('Portfolio data fetch cancelled after parallel stages');
    return null;
  }

  const totalRulesCount = getTotalRulesCount(rulesData);

  return {
    rulesData,
    consumedData,
    generatedData,
    tradeSummaryBatches,
    totalRulesCount,
    batchCount,
  };
};

/**
 * Fetch time-series data (stages 2-5) for a date-range change, reusing
 * pre-fetched meters. Called by the cache layer on a miss.
 * @param {object} args
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {Array}  args.meters - Pre-fetched meter objects
 * @param {object} args.dateSpan - { start, finish } Luxon DateTimes
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data (static fields)
 * @param {Function} [args.dispatchFunc] - Reducer dispatch
 * @param {object}   [args.cancelledRef] - Cancellation token
 * @param {Function} [args.onDataReceived] - Callback with (mergedData, tradeSummaryBatches, meters)
 * @param {Function} [args.onError] - Callback on error
 * @param {object}   [args.isFetchingRef] - Ref to prevent parallel fetches
 * @returns {Promise<void>}
 */
export const getTimeSeriesData = async (args) => {
  const {
    portfolioId, meters, dateSpan, timezone, environment,
    historianAggregation, tradeAggregation, initialData,
    dispatchFunc, cancelledRef, onDataReceived, onError, isFetchingRef,
  } = args;

  if (cancelledRef?.current) {
    logger.log('getTimeSeriesData cancelled before starting');
    return;
  }

  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping getTimeSeriesData - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    const result = await fetchTimeSeriesStages({
      portfolioId,
      meters,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      dispatchFunc,
      cancelledRef,
    });

    if (!result) return; // cancelled

    const mergedData = mergePortfolioBatchedData(
      initialData,
      meters,
      result.rulesData,
      result.consumedData,
      result.generatedData,
      result.tradeSummaryBatches,
    );

    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
        metersCount: meters.length,
        rulesCount: result.totalRulesCount,
        batchesLoaded: result.batchCount,
        batchesTotal: result.batchCount,
      });
    }

    if (onDataReceived) {
      onDataReceived(mergedData, result.tradeSummaryBatches, meters);
    }
  } catch (error) {
    logger.error('Error in getTimeSeriesData:', error);
    if (onError) {
      onError(error, 'Error fetching portfolio data. Please try again.');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};

/**
 * Main orchestrator: Fetch all portfolio data in batches using the 5-stage pattern.
 * Stage 1: Paginated meters
 * Stages 2-4: Rules, consumed, and generated per meter (parallel across stages)
 * Stage 5: Batched trade set summary at portfolio level
 * @param {object} args - Arguments for the orchestration
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.dateSpan - Date span { start, finish } (Luxon DateTimes)
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data (static fields)
 * @param {Function} args.onDataReceived - Callback with (mergedData, tradeSummaryBatches, meters)
 * @param {Function} args.onError - Callback on error
 * @param {Function} [args.dispatchFunc] - Reducer dispatch for loading state updates
 * @param {object} [args.isFetchingRef] - Ref to prevent parallel fetches
 * @param {object} [args.cancelledRef] - Ref whose .current indicates cancellation
 */
export const getPortfolioDataInBatches = async (args) => {
  const {
    portfolioId,
    dateSpan,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    onDataReceived,
    onError,
    dispatchFunc,
    isFetchingRef,
    cancelledRef,
  } = args;

  if (!portfolioId
    || !dateSpan
    || !timezone
    || !environment
    || !historianAggregation
    || !tradeAggregation) {
    if (onError) {
      onError(new Error('Missing required parameters for getPortfolioDataInBatches'));
    }
    return;
  }

  if (cancelledRef && cancelledRef.current) {
    logger.log('Skipping portfolio fetch - request already cancelled');
    return;
  }

  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping portfolio fetch - another fetch is already in progress');
    return;
  }

  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  try {
    if (cancelledRef?.current) {
      logger.log('getPortfolioDataInBatches cancelled before starting');
      return;
    }

    // Portfolio data comes from the Relay fragment (already available).
    // Advance the state machine past LOADING_PORTFOLIO so the checklist
    // shows "Portfolio loaded" while meters are fetching.
    if (dispatchFunc) {
      dispatchFunc({ type: PortfolioShowLoadingActions.SET_PORTFOLIO_LOADED });
    }

    // Stage 1: Fetch all meters (paginated) and rule count in parallel.
    // Always use METER_LIST_PAGE_SIZE (500) as the default. For portfolios
    // with fewer meters, the backend returns them all in one page. Only
    // portfolios exceeding 500 meters will paginate across multiple requests.
    const { start, finish } = dateSpan || {};
    const startSeconds = Math.floor(start.toSeconds());
    const finishSeconds = Math.floor(finish.toSeconds());

    const [{ meters }, ruleCount] = await Promise.all([
      getPortfolioMeters({
        portfolioId,
        environment,
        startSeconds,
        finishSeconds,
      }),
      getPortfolioRuleCount({
        portfolioId,
        environment,
        startSeconds,
        finishSeconds,
      }),
    ]);

    if (cancelledRef && cancelledRef.current) {
      logger.log('Cancelled after meters fetch, aborting');
      return;
    }

    if (meters.length === 0) {
      logger.log('No meters found, returning empty data');
      if (dispatchFunc) {
        dispatchFunc({
          type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
          metersCount: 0,
          rulesCount: 0,
          batchesLoaded: 0,
          batchesTotal: 0,
        });
      }
      if (onDataReceived) {
        onDataReceived(initialData, [], []);
      }
      return;
    }

    logger.log(`Stage 1 complete: ${meters.length} meters loaded, ruleCount=${ruleCount}`);
    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.SET_METERS_LOADED,
        metersCount: meters.length,
      });
      // Signal that meters are displayed and parallel stages can begin.
      // This sets activeStages to [RULES, CONSUMED_DATA, GENERATED_DATA]
      // so the checklist shows them as ACTIVE during loading.
      dispatchFunc({ type: PortfolioShowLoadingActions.METERS_DISPLAYED });
    }

    if (cancelledRef?.current) {
      logger.log('getPortfolioDataInBatches cancelled after meters fetch');
      return;
    }

    // Stages 2–5: delegate to the shared helper (also used by getTimeSeriesData)
    const result = await fetchTimeSeriesStages({
      portfolioId,
      meters,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      dispatchFunc,
      cancelledRef,
      ruleCount,
    });

    if (!result) return; // cancelled inside fetchTimeSeriesStages

    // Merge all stages into final data shape
    const mergedData = mergePortfolioBatchedData(
      initialData,
      meters,
      result.rulesData,
      result.consumedData,
      result.generatedData,
      result.tradeSummaryBatches,
    );

    if (dispatchFunc) {
      dispatchFunc({
        type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
        metersCount: meters.length,
        rulesCount: result.totalRulesCount,
        batchesLoaded: result.batchCount,
        batchesTotal: result.batchCount,
      });
    }

    if (cancelledRef && cancelledRef.current) {
      logger.log('Cancelled before delivering data, aborting');
      return;
    }

    if (onDataReceived) {
      onDataReceived(mergedData, result.tradeSummaryBatches, meters);
    }
  } catch (error) {
    logger.error('Error in portfolio data fetching:', error);
    if (onError) {
      onError(error, 'Error fetching portfolio data. Please try again.');
    }
  } finally {
    if (isFetchingRef) {
      isFetchingRef.current = false;
    }
  }
};
